import { BlogPost } from "@/data/blogPosts";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { generateProblemCopy, generateSolutionIntroCopy } from "@/utils/capsulaTemplates";

interface DiscoveryPathwayProps {
  articles: BlogPost[];
  capsulaTheme: string;
  problemStatement?: string;
}

export function DiscoveryPathway({
  articles,
  problemStatement,
}: DiscoveryPathwayProps) {
  // Sort articles by inspirationType if available
  const sortedArticles = [...articles].sort((a, b) => {
    const order = { "primer-paso": 1, "caso-practico": 2, "ejemplo-avanzado": 3 };
    const aOrder = a.inspirationType ? order[a.inspirationType] : 2;
    const bOrder = b.inspirationType ? order[b.inspirationType] : 2;
    return aOrder - bOrder;
  });

  return (
    <section id="discovery-articles" className="py-24 md:py-32 px-4 bg-background">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center space-y-12 mb-20 md:mb-28">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-tight">
            Mira lo que otros negocios como el tuyo ya están haciendo
          </h2>
          
          <div className="max-w-3xl mx-auto space-y-6">
            <p className="text-sm uppercase tracking-wider text-muted-foreground font-light">
              El problema que todos compartimos
            </p>
            <p className="text-2xl md:text-3xl lg:text-4xl font-light leading-tight text-foreground">
              "{problemStatement || "No tengo tiempo para crear contenido de calidad."}"
            </p>
          </div>
          
          <p className="text-lg md:text-xl font-light text-muted-foreground leading-relaxed">
            {generateSolutionIntroCopy(sortedArticles.length)}
          </p>
        </div>

        {/* Grid minimalista */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
          {sortedArticles.map((article) => (
            <Link 
              key={article.id}
              to={`/blog/${article.slug}`}
              className="group block"
            >
              <div className="p-8 md:p-10 space-y-6 bg-secondary/20 dark:bg-secondary/10 rounded-2xl hover:bg-secondary/30 dark:hover:bg-secondary/20 transition-all duration-300">
                
                <h3 className="text-2xl md:text-3xl font-light tracking-tight leading-tight group-hover:text-primary transition-colors">
                  {article.title}
                </h3>
                
                <p className="text-base md:text-lg font-light text-muted-foreground leading-relaxed">
                  {article.businessSolution || article.description}
                </p>
                
                <div className="space-y-3 text-sm text-muted-foreground">
                  {article.realWorldExample && (
                    <p className="flex items-start gap-2">
                      <span>💡</span>
                      <span>{article.realWorldExample}</span>
                    </p>
                  )}
                  
                  <p className="flex items-center gap-2">
                    <span>⏱️</span>
                    <span>{article.avgImplementationTime || "30 minutos"}</span>
                  </p>
                </div>
                
                <div className="flex items-center gap-2 text-primary text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  <span>Ver más</span>
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
